import * as React from "react";
import { useState } from "react";
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { ChatCircleIcon, PlayCircleIcon, XIcon } from "@phosphor-icons/react";

import { CallLog } from "@/hooks/api/use-call-logs";

import { CallInsightsPanel } from "./call-insights-panel";
import { CallLogAudioPlayer } from "./call-log-audio-player";
import { ConversationTranscript } from "./conversation-transcript";

interface CallLogModalProps {
	open: boolean;
	onClose: () => void;
	callLog: CallLog | null;
	/** Overrides the heading — the SMB app names the person the call was with. */
	title?: string;
	/** Reads the call back for the business rather than the operator. See {@link CallInsightsPanel}. */
	plain?: boolean;
}

/**
 * A call, read back: the recording, the analysis, and the conversation it produced.
 *
 * The bubbles and the fetch machinery live in {@link ConversationTranscript} and the player in
 * {@link CallLogAudioPlayer}, both shared with the Proving Ground. What stays here is the
 * chrome and the playhead, which is the one piece of state the other two have to agree on.
 */
export function CallLogModal({ open, onClose, callLog, title, plain = false }: CallLogModalProps): React.JSX.Element {
	const [showAudioPlayer, setShowAudioPlayer] = useState(false);
	const [currentTime, setCurrentTime] = React.useState(0);

	const currentTimeRef = React.useRef(0);
	const previousUpdateTimeRef = React.useRef(0);

	React.useEffect(() => {
		if (!open) {
			setShowAudioPlayer(false);
			setCurrentTime(0);
			currentTimeRef.current = 0;
			previousUpdateTimeRef.current = 0;
		}
	}, [open]);

	if (!callLog) return <></>;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			sx={{
				"& .MuiDialog-paper": {
					height: "80vh",
					maxHeight: "800px",
				},
			}}
		>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					pb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<ChatCircleIcon size={24} color="var(--mui-palette-primary-main)" />
					<Typography variant="h6" fontSize={18}>
						{title ?? "Conversation Log"}
					</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Tooltip title={showAudioPlayer ? "Hide Recording" : "Show Recording"}>
						<IconButton
							onClick={() => {
								setShowAudioPlayer(!showAudioPlayer);
							}}
							size="small"
						>
							<PlayCircleIcon
								size={20}
								weight="fill"
								color={showAudioPlayer ? "var(--mui-palette-primary-main)" : "var(--mui-palette-info-light)"}
							/>
						</IconButton>
					</Tooltip>
					<IconButton onClick={onClose} size="small">
						<XIcon size={20} />
					</IconButton>
				</Box>
			</DialogTitle>

			<Divider sx={{ mt: 1 }} />

			<CallLogAudioPlayer
				recordingUrl={`/calls/${callLog.id}/recording`}
				show={showAudioPlayer}
				onTimeUpdate={(time) => {
					currentTimeRef.current = time;

					const currentTimeMs = time * 1000;
					const previousTimeMs = previousUpdateTimeRef.current;

					// Only update state if 200ms or more have passed
					if (Math.abs(currentTimeMs - previousTimeMs) >= 200) {
						previousUpdateTimeRef.current = currentTimeMs;
						setCurrentTime(time);
					}
				}}
			/>

			<DialogContent sx={{ p: 0, display: "flex", flexDirection: { xs: "column", md: "row" } }}>
				<CallInsightsPanel callLog={callLog} plain={plain} />
				<ConversationTranscript
					conversationId={callLog.conversationId ?? null}
					currentTime={currentTime}
					currentTimeRef={currentTimeRef}
					active={open}
					emptyMessage={plain ? "Nothing was said on this call" : undefined}
					plain={plain}
				/>
			</DialogContent>
		</Dialog>
	);
}
